import * as React from 'react';
import * as _ from 'lodash';
import * as moment from 'moment';
import { Dispatch, connect } from 'react-redux';
import { IReportState, IApplicationState, AppActionCreators, ReportActionCreators, IPatientOutcomesReportResponse } from '@nkr/store/index';
import { IPatientOutcomes } from '@nkr/models/index';
import { HeaderWrapper } from './styles';
import { Button, FormControl, Well } from 'react-bootstrap';
import { ExcelExportParams, GridApi, GridReadyEvent, ProcessCellForExportParams } from 'ag-grid-community';
import PatientOutComesReportGrid from './patient-outcomes-grid';

interface IOwnProps { }

interface IStateProps {
    reports: IReportState
}

interface IDispatchProps {
    showLoading: (message?: string) => Promise<void>,
    hideLoading: () => void,
    getPatientOutcomesReport: () => Promise<IPatientOutcomesReportResponse>
}

type TProps = IStateProps & IDispatchProps & IOwnProps

interface IState {
    report: IPatientOutcomes[],
    gridApi: GridApi,
    centerType: string,
    center: number,
    recipientType: number
}

class PatientOutComesReport extends React.Component<TProps, IState> {
    readonly state: IState = {
        report: [],
        gridApi: null,
        centerType: 'R',
        center: 0,
        recipientType: 0
    }

    onGridReady = (e: GridReadyEvent) => {
        this.setState({
            gridApi: e.api
        });
    }

    componentDidMount = () => {
        this.getData();
    }

    getData = () => {
        this.props.showLoading('Getting report data, one moment please...').then(() => {
            this.props.getPatientOutcomesReport()
                .then(response => {
                    this.setState({
                        report: response.data.results
                    });
                })
                .catch((ex) => {
                    alert('An error occured while loading report data');

                })
                .finally(() => {
                    this.props.hideLoading();
                });
        });
    }

    getLookupData = () => {
        let recipientCenters = this.state.report.map(x => ({ id: x.recipientCenterId, alias: x.recipientCenter }));
        let donorCenters = this.state.report.map(x => ({ id: x.donorCenterId, alias: x.donorCenter }));

        let centers = _.uniqBy([...recipientCenters, ...donorCenters], x => x.id);
        let centerList = _.sortBy(centers, x => x.alias);

        let types = _.uniqBy(this.state.report.map(x => ({ id: x.recipientTypeId, description: x.recipientType })), x => x.id);
        let recipientTypes = _.sortBy(types, x => x.id);

        return { centerList, recipientTypes };
    }

    getFilteredList = () => {
        let list = _.cloneDeep(this.state.report);

        if (this.state.centerType == 'R' && this.state.center > 0) {
            list = list.filter(x => x.recipientCenterId == this.state.center);
        }

        if (this.state.centerType == 'D' && this.state.center > 0) {
            list = list.filter(x => x.donorCenterId == this.state.center);
        }

        if (this.state.recipientType > 0) {
            list = list.filter(x => x.recipientTypeId == this.state.recipientType);
        }

        return list;
    }

    handleCenterTypeChange = (e: React.FormEvent<FormControl & HTMLSelectElement>) => {
        this.setState({
            centerType: e.currentTarget.value
        });
    }

    handleCenterChange = (e: React.FormEvent<FormControl & HTMLSelectElement>) => {
        this.setState({
            center: parseInt(e.currentTarget.value)
        });
    }

    handleRecipientTypeChange = (e: React.FormEvent<FormControl & HTMLSelectElement>) => {
        this.setState({
            recipientType: parseInt(e.currentTarget.value)
        });
    }

    handleExport = () => {
        let params: ExcelExportParams = {
            columnGroups: true,
            fileName: 'patient-outcomes.xlsx',
            processCellCallback: (params: ProcessCellForExportParams) => {
                let colDef = params.column.getColDef();

                if (colDef.headerName == 'Rank')
                    return params.node.rowIndex + 1;
                else if (colDef.colId == 'xpDate')
                    return moment(params.value).format('MM/DD/YYYY');

                return params.value;
            }
        };

        this.state.gridApi.exportDataAsExcel(params);
    };

    render = () => {
        const { centerList, recipientTypes } = this.getLookupData();

        const filteredList = this.getFilteredList();

        return (
            <>
                <HeaderWrapper className='container-fluid'>
                    <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 20 }}>
                        <div className='billing-header'>
                            Patient Outcomes Report
                        </div>
                        <div style={{ display: 'flex' }}></div>
                        <div>
                            <Button onClick={this.handleExport}>Export CSV</Button>
                        </div>
                    </div>
                </HeaderWrapper>

                <Well bsSize='sm'>
                    <div style={{ display: 'flex', alignItems: 'center' }}>
                        <div style={{ paddingRight: 5 }}>
                            <FormControl
                                componentClass='select'
                                value={this.state.centerType}
                                onChange={this.handleCenterTypeChange}
                            >
                                <option value='R'>Recipient</option>
                                <option value='D'>Donor</option>
                            </FormControl>
                        </div>
                        <div style={{ paddingRight: 5 }}>Center:</div>
                        <div style={{ marginRight: 50 }}>
                            <FormControl
                                componentClass='select'
                                value={this.state.center}
                                onChange={this.handleCenterChange}
                            >
                                <option value={''}>- All Centers -</option>
                                {centerList.map((center, index) =>
                                    <option key={'ctr-' + index} value={center.id}>{center.alias}</option>
                                )}
                            </FormControl>
                        </div>

                        <div style={{ paddingRight: 5 }}>Recipient Type:</div>
                        <div>
                            <FormControl
                                componentClass='select'
                                value={this.state.recipientType}
                                onChange={this.handleRecipientTypeChange}
                            >
                                <option value={''}>- All Types -</option>
                                {recipientTypes.map((type, index) =>
                                    <option key={'rc-' + index} value={type.id}>{type.description}</option>
                                )}
                            </FormControl>
                        </div>
                    </div>
                </Well>

                <div className='row'>
                    <div className='col-sm-12'>
                        <PatientOutComesReportGrid
                            data={filteredList}
                            onGridReady={this.onGridReady}
                        />
                    </div>
                </div>
            </>
        );
    }
}

const mapStateToProps = (appState: IApplicationState): IStateProps => {
    return {
        reports: appState.reports
    }
}

const mapDispatchToProps = (dispatch: Dispatch<IApplicationState>): IDispatchProps => {
    return {
        showLoading: (message) => {
            return dispatch(AppActionCreators.showLoadingAsync(message));
        },
        hideLoading: () => {
            dispatch(AppActionCreators.hideLoading());
        },
        getPatientOutcomesReport: () => {
            return dispatch(ReportActionCreators.getPatientOutcomesReport());
        }
    }
}

export default connect<IStateProps, IDispatchProps, IOwnProps>(
    mapStateToProps,
    mapDispatchToProps)(PatientOutComesReport);